import { getData, postData, putData, patchData, deleteData } from './FetchNodeServices.js';
import type { MockProformaInvoice } from '@/mocks/billing';

export interface ProformaPayload {
  piNumber?: string;
  customerId: string;
  customerName: string;
  customerGSTIN?: string;
  customerPhone?: string;
  customerEmail?: string;
  billingAddress?: string;
  shippingAddress?: string;
  warehouseId?: string;
  date?: string;
  validUntil?: string;
  validityDays?: number;
  status?: string;
  fromQuotationId?: string;
  fromQuotationNumber?: string;
  convertedToInvoiceId?: string;
  convertedToInvoiceNumber?: string;
  subtotal: number;
  discountAmount: number;
  taxableAmount: number;
  cgst: number;
  sgst: number;
  igst: number;
  taxAmount: number;
  totalAmount: number;
  expectedAdvance?: number;
  balanceDue?: number;
  notes?: string;
  paymentTerms?: string;
  deliveryTerms?: string;
  bankDetails?: string;
  termsAndConditions?: string;
  preparedBy?: string;
  items: Array<{
    itemId?: string;
    itemName: string;
    hsnCode?: string;
    description?: string;
    qty: number;
    unit?: string;
    rate: number;
    discountPercent?: number;
    discountAmount?: number;
    taxableAmount?: number;
    taxRate?: number;
    cgst?: number;
    sgst?: number;
    igst?: number;
    totalAmount?: number;
  }>;
}

export const proformaService = {
  listProformaInvoices: async (params?: {
    search?: string;
    status?: string;
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }): Promise<MockProformaInvoice[]> => {
    try {
      const res = await getData('api/v1/proforma', params || {});
      if (res && res.success && Array.isArray(res.data)) {
        return res.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching proforma invoices:', error);
      throw error;
    }
  },

  getProformaInvoice: async (id: string): Promise<MockProformaInvoice> => {
    try {
      const res = await getData(`api/v1/proforma/${id}`);
      if (res && res.success && res.data) {
        return res.data;
      }
      throw new Error(res?.message || 'Failed to fetch proforma invoice');
    } catch (error) {
      console.error('Error fetching proforma invoice detail:', error);
      throw error;
    }
  },

  createProformaInvoice: async (payload: ProformaPayload): Promise<{ id: string; piNumber: string }> => {
    try {
      const res = await postData('api/v1/proforma', payload);
      if (res && res.success && res.data) {
        return res.data;
      }
      throw new Error(res?.message || 'Failed to create proforma invoice');
    } catch (error) {
      console.error('Error creating proforma invoice:', error);
      throw error;
    }
  },

  updateProformaInvoice: async (id: string, payload: ProformaPayload): Promise<{ id: string; piNumber: string }> => {
    try {
      const res = await putData(`api/v1/proforma/${id}`, payload);
      if (res && res.success && res.data) {
        return res.data;
      }
      throw new Error(res?.message || 'Failed to update proforma invoice');
    } catch (error) {
      console.error('Error updating proforma invoice:', error);
      throw error;
    }
  },

  updateStatus: async (
    id: string,
    status: string,
    extra?: { convertedToInvoiceId?: string; convertedToInvoiceNumber?: string }
  ): Promise<boolean> => {
    try {
      const res = await patchData(`api/v1/proforma/${id}/status`, { status, ...extra });
      return !!(res && res.success);
    } catch (error) {
      console.error('Error updating proforma status:', error);
      throw error;
    }
  },

  deleteProformaInvoice: async (id: string): Promise<boolean> => {
    try {
      const res = await deleteData(`api/v1/proforma/${id}`);
      return !!(res && res.success);
    } catch (error) {
      console.error('Error deleting proforma invoice:', error);
      throw error;
    }
  },
};
